// Styles
import "./account-row.scss";
// Tools
import { useState, type MouseEvent } from "react";
import { createPortal } from "react-dom";
// Components
import { StatusChip } from "../../../../../../../../../../shared/components/status-chip/StatusChip";
import type { AccountCertChip } from "../../utils/account-certificates";
import type {
	CertPresence,
	CertPresenceAction,
} from "../../utils/cert-presence";

interface Props {
	title: string;
	id: string;
	status: string;
	cost: number;
	certsChips: AccountCertChip[];
	certsMore?: number;
	certsMoreTooltip?: string;
	/** Empty Certs-column presence (DAI-1201). Ignored when chips are present. */
	certPresence?: CertPresence;
	dimmed?: boolean;
	onOpen?: () => void;
}

type TipPos = { left: number; top: number; below: boolean; text: string };

function actionIcon(action: CertPresenceAction): {
	src: string;
	aria: string;
} {
	if (action === "add") {
		return { src: "/img/icons/add.png", aria: "Додати сертифікати" };
	}
	return { src: "/img/icons/edit.png", aria: "Редагувати сертифікати" };
}

export const AccountRow = ({
	title,
	id,
	status,
	cost,
	certsChips,
	certsMore = 0,
	certsMoreTooltip = "",
	certPresence,
	dimmed,
	onOpen,
}: Props) => {
	const hasCerts = certsChips.length > 0;
	const [tip, setTip] = useState<TipPos | null>(null);

	const presence = certPresence ?? {
		kind: "loading" as const,
		emptyLabel: "—",
		hint: "",
		action: "edit" as const,
	};
	const { src: actionSrc, aria: actionAria } = actionIcon(presence.action);

	const handleEditClick = (e: MouseEvent) => {
		e.stopPropagation();
		onOpen?.();
	};

	const showTooltip = (el: HTMLElement, text: string) => {
		if (!text) return;
		const rect = el.getBoundingClientRect();
		const gap = 8;
		const below = rect.top <= 160;
		setTip({
			left: rect.left + rect.width / 2,
			top: below ? rect.bottom + gap : rect.top - gap,
			below,
			text,
		});
	};

	const emptyKindClass =
		presence.kind !== "loading" && presence.kind !== "has_certs"
			? ` account-row__certs-empty--${presence.kind}`
			: "";

	return (
		<div
			className={`account-row${dimmed ? " account-row--dimmed" : ""}`}>
			<div className='account-row__wrapper'>
				<div className='account-row__name'>
					<div className='account-row__name_title break-text' data-copyable>
						{title}
					</div>
					<div className='account-row__name_id break-text' data-copyable>
						{id}
					</div>
				</div>
				<div className='account-row__status'>
					<StatusChip status={status} />
				</div>
				<div className='account-row__certs'>
					{hasCerts ? (
						<div className='account-row__certs-chips'>
							{certsChips.map((chip) => (
								<span
									key={chip.key}
									className={`account-row__cert-chip${chip.pending ? " account-row__cert-chip--pending" : ""}${chip.key.startsWith("group-") ? " account-row__cert-chip--group" : ""}`}
									onMouseEnter={(e) =>
										showTooltip(e.currentTarget, chip.tooltip)
									}
									onMouseLeave={() => setTip(null)}>
									{chip.label}
								</span>
							))}
							{certsMore > 0 && (
								<span
									className='account-row__cert-chip account-row__cert-chip--more'
									onMouseEnter={(e) =>
										showTooltip(e.currentTarget, certsMoreTooltip)
									}
									onMouseLeave={() => setTip(null)}>
									+{certsMore}
								</span>
							)}
						</div>
					) : (
						<span
							className={`account-row__certs-empty${emptyKindClass}`}
							onMouseEnter={(e) =>
								showTooltip(e.currentTarget, presence.hint)
							}
							onMouseLeave={() => setTip(null)}>
							{presence.emptyLabel}
						</span>
					)}
					{onOpen ? (
						<button
							type='button'
							className={`account-row__certs-edit${presence.action === "add" ? " account-row__certs-edit--add" : ""}`}
							onClick={handleEditClick}
							aria-label={`${actionAria} ${title}`}
							title={presence.hint || undefined}>
							<img src={actionSrc} alt='' width={12} height={12} />
						</button>
					) : null}
				</div>
				{tip
					? createPortal(
							<div
								className={`account-row__certs-tooltip${tip.below ? " account-row__certs-tooltip--below" : ""}`}
								style={{ left: tip.left, top: tip.top }}
								role='tooltip'>
								{tip.text}
							</div>,
							document.body,
						)
					: null}
				<div className='account-row__cost' data-copyable>
					<span data-copy-exclude>$</span>
					{Number(cost).toFixed(2)}
				</div>
			</div>
		</div>
	);
};
